import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MIN_PASSWORD_LENGTH, authResponseSchema } from '@competa/shared';
import { apiRequest } from '../../api/client';
import { Button } from '../../components/Button';
import { TextField } from '../../components/TextField';
import { errorMessage, fieldErrors } from '../../i18n/errors';
import { useAuth } from './AuthContext';
import styles from './LoginPage.module.css';

/**
 * Formular zum Ändern des eigenen Passworts (Einstellungen und erzwungener Wechsel).
 * Prüft die Wiederholung im Browser, die Passwortregel verbindlich der Server; dessen Feldfehler
 * werden am jeweiligen Feld angezeigt.
 */
export function ChangePasswordForm() {
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * Sendet die Passwortänderung und übernimmt das aktualisierte Profil.
   * @param event Absenden des Formulars.
   */
  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSuccess(false);
    setGeneralError(null);
    if (next !== confirm) {
      setErrors({ confirm: t('fields.mismatch') });
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const response = await apiRequest('PUT', '/auth/password', {
        body: { currentPassword: current, newPassword: next },
        schema: authResponseSchema,
      });
      setUser(response.user);
      setCurrent('');
      setNext('');
      setConfirm('');
      setSuccess(true);
    } catch (caught) {
      const fields = fieldErrors(t, caught);
      setErrors({ current: fields.currentPassword ?? '', next: fields.newPassword ?? '' });
      setGeneralError(Object.keys(fields).length === 0 ? errorMessage(t, caught) : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} noValidate>
      <TextField
        label={t('password.current')}
        type="password"
        value={current}
        onChange={(event) => setCurrent(event.target.value)}
        autoComplete="current-password"
        error={errors.current || undefined}
        required
      />
      <TextField
        label={t('password.new')}
        type="password"
        value={next}
        onChange={(event) => setNext(event.target.value)}
        autoComplete="new-password"
        hint={t('password.hint', { min: MIN_PASSWORD_LENGTH })}
        error={errors.next || undefined}
        required
      />
      <TextField
        label={t('password.confirm')}
        type="password"
        value={confirm}
        onChange={(event) => setConfirm(event.target.value)}
        autoComplete="new-password"
        error={errors.confirm || undefined}
        required
      />
      {generalError && (
        <p role="alert" className={styles.error}>
          {generalError}
        </p>
      )}
      {success && <p role="status">{t('password.success')}</p>}
      <Button type="submit" disabled={busy || !current || !next || !confirm}>
        {t('password.submit')}
      </Button>
    </form>
  );
}
